/**
 * Blog Service
 * Handles blog posts from Neon database via API
 * Uses Vercel serverless functions in production, Express server in development
 */

// Helper to get API URL
function getApiUrl(): string {
  // In production (Vercel), use relative paths for serverless functions
  if (process.env.NODE_ENV === 'production') {
    return ''; // Relative path - will use Vercel serverless functions
  }
  // In development, use the Express server
  return process.env.REACT_APP_API_URL || 'http://localhost:3001';
}

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  author: string;
  publishedAt: string;
  category: 'tutorial' | 'tips' | 'tools' | 'shorts-boost';
  tags: string[];
  featuredImage?: string;
  readTime: number;
  enableInProd?: boolean;
  views?: number;
}

export interface BlogCategory {
  id: string;
  name: string;
  description: string;
  color: string;
}

// Legacy response interfaces (kept for compatibility, not used)
export interface BlogPostsResponse {
  success: boolean;
  posts: BlogPost[];
  total: number;
}

export interface BlogPostResponse {
  success: boolean;
  post: BlogPost;
}

export interface BlogCategoriesResponse {
  success: boolean;
  categories: BlogCategory[];
}

/**
 * Get all blog posts with optional filtering
 * Fetches from Neon database via API
 * Returns a Promise that resolves to BlogPost[]
 */
export async function getBlogPosts(params?: {
  category?: string;
  search?: string;
  limit?: number;
  offset?: number;
  includeUnpublished?: boolean;
}): Promise<BlogPost[]> {
  const apiUrl = getApiUrl();
  const queryParams = new URLSearchParams();
  
  if (params?.category) queryParams.append('category', params.category);
  if (params?.search) queryParams.append('search', params.search);
  if (params?.limit) queryParams.append('limit', String(params.limit));
  if (params?.offset) queryParams.append('offset', String(params.offset));
  if (params?.includeUnpublished) queryParams.append('includeUnpublished', 'true');
  
  try {
    const url = `${apiUrl}/api/blog/posts?${queryParams.toString()}`;
    
    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      let errorMessage = `Failed to fetch blog posts: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (e) {
        // Ignore JSON parse errors
      }
      console.error('Error fetching blog posts:', errorMessage);
      return []; // Return empty array on error
    }
    
    const data: BlogPostsResponse = await response.json();
    return data.posts || [];
  } catch (error) {
    // Handle AbortError (timeout)
    if (error instanceof Error && (error.name === 'AbortError' || error.message.includes('aborted'))) {
      console.error('Request timeout while fetching blog posts');
      return [];
    }
    // Handle network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      console.error('Network error while fetching blog posts');
      return [];
    }
    console.error('Error fetching blog posts:', error);
    return []; // Return empty array on error
  }
}

/**
 * Get a single blog post by slug
 * Fetches from Neon database via API
 * Returns a Promise that resolves to BlogPost | null
 */
export async function getBlogPostBySlug(slug: string): Promise<BlogPost | null> {
  const apiUrl = getApiUrl();
  
  try {
    const url = `${apiUrl}/api/blog/posts/${encodeURIComponent(slug)}`;
    
    // Create abort controller for timeout (fallback for browsers without AbortSignal.timeout)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    
    // Check if response is HTML (which means routing failed)
    const contentType = response.headers.get('content-type');
    if (contentType && !contentType.includes('application/json')) {
      const text = await response.text();
      if (text.trim().toLowerCase().startsWith('<!doctype') || text.trim().toLowerCase().startsWith('<html')) {
        console.error('API returned HTML instead of JSON. This usually means the API route is not configured correctly in Vercel.');
        throw new Error('API route not found. Please check Vercel serverless function configuration.');
      }
    }
    
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      // Try to get error message from response
      let errorMessage = `Failed to fetch blog post: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (e) {
        // If JSON parse fails, check if it's HTML
        if (e instanceof SyntaxError) {
          throw new Error('API returned invalid response. Please check server configuration.');
        }
      }
      throw new Error(errorMessage);
    }
    
    const data: BlogPostResponse = await response.json();
    if (!data.success || !data.post) {
      return null;
    }
    return data.post;
  } catch (error) {
    // Handle AbortError (timeout)
    if (error instanceof Error && (error.name === 'AbortError' || error.message.includes('aborted'))) {
      console.error('Request timeout while fetching blog post:', slug);
      throw new Error('Request timeout. Please try again.');
    }
    // Handle network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      console.error('Network error while fetching blog post:', slug);
      throw new Error('Network error. Please check your connection.');
    }
    console.error('Error fetching blog post:', error);
    throw error; // Re-throw to let caller handle it
  }
}

/**
 * Get all blog categories
 * Fetches from API (static data, but via API for consistency)
 * Returns a Promise that resolves to BlogCategory[]
 */
export async function getBlogCategories(): Promise<BlogCategory[]> {
  const apiUrl = getApiUrl();
  
  try {
    const response = await fetch(`${apiUrl}/api/blog/categories`);
    if (!response.ok) {
      throw new Error(`Failed to fetch categories: ${response.status}`);
    }
    const data: BlogCategoriesResponse = await response.json();
    return data.categories;
  } catch (error) {
    console.error('Error fetching categories:', error);
    return []; // Return empty array on error
  }
}

/**
 * Search blog posts
 * Uses getBlogPosts with search parameter
 * Returns a Promise that resolves to BlogPost[]
 */
export async function searchBlogPosts(searchTerm: string, category?: string): Promise<BlogPost[]> {
  return getBlogPosts({ search: searchTerm, category });
}

/**
 * Get related posts based on category and tags
 * Fetches all posts and filters by category/tags
 * Returns a Promise that resolves to BlogPost[]
 */
export async function getRelatedPosts(currentPost: BlogPost, limit: number = 3): Promise<BlogPost[]> {
  try {
    const allPosts = await getBlogPosts();
    const relatedPosts: BlogPost[] = [];
    const usedIds = new Set([currentPost.id]);
    
    // First, try to find posts with same category
    const sameCategoryPosts = allPosts
      .filter(p => p.category === currentPost.category && p.id !== currentPost.id)
      .slice(0, limit);
    
    relatedPosts.push(...sameCategoryPosts);
    sameCategoryPosts.forEach(p => usedIds.add(p.id));
    
    // If we need more, find posts with shared tags
    if (relatedPosts.length < limit) {
      const sharedTagPosts = allPosts
        .filter(p => {
          if (usedIds.has(p.id)) return false;
          return p.tags.some(tag => currentPost.tags.includes(tag));
        })
        .slice(0, limit - relatedPosts.length);
      
      relatedPosts.push(...sharedTagPosts);
    }
    
    return relatedPosts.slice(0, limit);
  } catch (error) {
    console.error('Error fetching related posts:', error);
    return [];
  }
}
